<script setup lang="ts">
/**
 * H14 — Search drawer «Preview results» panel (States 2–5)
 * Desktop `50:10145`: left column 372, 2-col grid, sticky «See all N results».
 * Mobile `50:10483`: horizontal card row; See all lives in the drawer footer.
 * State 5 `50:10235` / `50:10486`: 0 results + «Or browse:» Links (desktop) / Chips (mobile).
 */
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairProductCardV2 } from '@/ui/patterns/FairProductCardV2'
import type {
  FairHomeV2SearchBrowseCategory,
  FairHomeV2SearchDrawerMode,
  FairHomeV2SearchPreviewCard,
} from './fair-home-v2-search-drawer.data'

withDefaults(
  defineProps<{
    mode: FairHomeV2SearchDrawerMode
    cards: FairHomeV2SearchPreviewCard[]
    total: number | null
    loading?: boolean
    compact?: boolean
    browseCategories?: FairHomeV2SearchBrowseCategory[]
    /** Mobile: See all is rendered by the drawer as a sticky footer. */
    showSeeAll?: boolean
  }>(),
  {
    loading: false,
    compact: false,
    browseCategories: () => [],
    showSeeAll: true,
  },
)

const emit = defineEmits<{
  'see-all': []
  browse: [category: FairHomeV2SearchBrowseCategory]
  'card-click': [card: FairHomeV2SearchPreviewCard]
}>()

const { t } = useI18n()

function productTo(card: FairHomeV2SearchPreviewCard) {
  if (card.isDemo) return undefined
  return {
    name: 'product' as const,
    params: {
      productId: String(card.id),
      productVariantId: String(card.variantId),
    },
  }
}
</script>

<template>
  <section
    class="fair-home-v2-search__preview"
    :class="{ 'fair-home-v2-search__preview--compact': compact }"
    :aria-busy="loading"
    :data-figma="compact ? '50:10483' : '50:10145'"
  >
    <div class="fair-home-v2-search__preview-head">
      <span class="fair-home-v2-search__preview-label">
        {{ t('fair_home_v2.search.preview_results') }}
      </span>
      <span class="fair-home-v2-search__preview-count">
        {{ t('fair_home_v2.search.results_count', { count: total ?? 0 }) }}
      </span>
    </div>

    <!-- State 5 — Empty -->
    <div
      v-if="mode === 'empty'"
      class="fair-home-v2-search__empty"
    >
      <FairIcon
        name="search"
        :size="60"
        class="fair-home-v2-search__empty-icon"
      />
      <p class="fair-home-v2-search__empty-title">
        {{ t('fair_home_v2.search.empty_title') }}
      </p>
      <p class="fair-home-v2-search__empty-body">
        {{ t('fair_home_v2.search.empty_body') }}
      </p>

      <div
        v-if="browseCategories.length"
        class="fair-home-v2-search__browse"
      >
        <span class="fair-home-v2-search__browse-label">
          {{ t('fair_home_v2.search.or_browse') }}
        </span>
        <div class="fair-home-v2-search__browse-list">
          <button
            v-for="category in browseCategories"
            :key="category.id"
            type="button"
            class="fair-home-v2-search__browse-item"
            @click="emit('browse', category)"
          >
            {{ category.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- States 2–4 — cards -->
    <div
      v-else
      class="fair-home-v2-search__cards"
    >
      <FairProductCardV2
        v-for="(card, index) in cards"
        :key="card.id"
        class="fair-home-v2-search__card"
        :size="compact ? 'mobile' : 'default'"
        :brand="card.brand"
        :title="card.title"
        :price="card.price"
        :from-label="t('fair_product.price_from_short')"
        :images="card.images"
        :colors="card.colors ?? []"
        :colors-total="card.colorsTotal"
        :favorite="Boolean(card.isFavorite)"
        :to="productTo(card)"
        :priority="index < 2"
        @click="emit('card-click', card)"
      />
    </div>

    <div
      v-if="showSeeAll && mode !== 'empty' && (total ?? 0) > 0"
      class="fair-home-v2-search__see-all-wrap"
    >
      <FairButton
        class="fair-home-v2-search__see-all"
        variant="primary"
        size="m"
        :rounded="false"
        trailing-icon="arrow-right"
        :label="t('fair_home_v2.search.see_all', { count: total ?? 0 })"
        @click="emit('see-all')"
      />
    </div>
  </section>
</template>
